import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Radar, Search, Network, GitCompareArrows, ClipboardCheck,
  Table2, Cpu, LogOut, ShieldCheck, ShieldAlert, Users,
} from "lucide-react";

const NAV = [
  { to: "/app/dashboard", label: "Overview", icon: Radar },
  { to: "/app/search", label: "NGO Search", icon: Search },
  { to: "/app/mapping", label: "Crosswalk Map", icon: Table2 },
];

const ADMIN_NAV = [
  { to: "/app/candidates", label: "Unverified Links", icon: GitCompareArrows },
  { to: "/app/verify", label: "Verification", icon: ShieldCheck },
  { to: "/app/review", label: "Review Queue", icon: ClipboardCheck },
  { to: "/app/allowed-users", label: "Allowed Users", icon: Users },
  { to: "/app/jobs", label: "Sync Jobs", icon: Cpu },
  { to: "/app/activity", label: "Activity", icon: ShieldAlert },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-blue-500/15 text-blue-300 border border-blue-700/40"
        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
    }`;

  return (
    <div className="radar-grain min-h-screen flex bg-slate-950">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-slate-800/80 bg-slate-950/80 backdrop-blur-xl sticky top-0 h-screen z-20">
        <button
          onClick={() => navigate("/app/dashboard")}
          className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-800/80"
          data-testid="brand-home-btn"
        >
          <Radar className="w-6 h-6 text-blue-400" />
          <div className="text-left leading-none">
            <div className="font-extrabold tracking-tight text-slate-100 text-lg">RADAR</div>
            <div className="font-mono text-[9px] uppercase tracking-widest text-slate-500">NGO Crosswalk</div>
          </div>
        </button>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <div className="px-3 pt-5 pb-1 font-mono text-[10px] uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" /> Admin
              </div>
              {ADMIN_NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.replace(/\s/g, "-").toLowerCase()}`}>
                  <n.icon className="w-4 h-4" /> {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="border-t border-slate-800/80 p-3">
          <div className="flex items-center gap-2.5 px-2 py-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-700" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-500/20 text-blue-300 grid place-items-center font-semibold text-xs">
                {user?.name?.[0] || user?.email?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-200">{user?.name || "User"}</div>
              <div className="truncate font-mono text-[10px] text-slate-500">{isAdmin ? "Administrator" : "Viewer"}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors duration-150"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="md:hidden sticky top-0 z-20 flex items-center justify-between border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-xl px-4 h-14">
          <button onClick={() => navigate("/app/dashboard")} className="flex items-center gap-2">
            <Radar className="w-5 h-5 text-blue-400" />
            <span className="font-extrabold tracking-tight text-slate-100">RADAR</span>
          </button>
          <button onClick={logout} data-testid="logout-btn-mobile" className="text-slate-400">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
        <main className="relative z-10 flex-1 px-4 sm:px-8 py-6 sm:py-10 max-w-[1500px] w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
